import { toast } from 'react-toastify';
import appApi from '../../../../api/app-api';
import { displayErrorText } from '../../../../api/errors/api-error-codes';
import { JournalEntryDatRecordInput } from '../../../../api/types/accounting/journal-entries/journal-entry-dat-record-input';
import generateAndDownloadExcelFile from '../../../../component/excel/generate-and-download-excel-file';

/** Format `YYYY-MM-DD` / Date without timezone shifts for display. */
const formatExportDate = (value: string | Date | null | undefined): string => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    return value.trim();
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = String(d.getUTCFullYear());
  return `${day}/${month}/${year}`;
};

const formatAmount = (amount: number | null | undefined): string => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '';
  return amount.toFixed(2);
};

// Same column order/headers as the flat-data PDF export.
const HEADERS = [
  'מס״ד',
  'חובה',
  'זכות',
  'פרטים',
  'ת.פעולה',
  'ת.ערך',
  'סכום',
  'אסמכה 1',
  'אסמכתה 2',
] as const;

const buildRows = (
  records: JournalEntryDatRecordInput[],
): (string | number)[][] =>
  records.map((r, index) => [
    index + 1,
    r.debitAccountCode ?? '',
    r.creditAccountCode ?? '',
    r.paymentDescription ?? '',
    formatExportDate(r.entryDate),
    formatExportDate(r.valueDate),
    formatAmount(r.amount),
    r.documentNumber ?? '',
    r.paymentReference ?? '',
  ]);

/**
 * Fetches the same flat DAT-oriented records used by the PDF export, then
 * downloads them as a single Excel sheet.
 */
export const exportJournalEntryReportFlatDataExcelOperation = async (
  reportId: number,
  setLoading: (loading: boolean) => void,
): Promise<boolean> => {
  if (!reportId) {
    toast.warn('חסר מזהה דיווח לייצוא');
    return false;
  }

  try {
    setLoading(true);

    const res = await appApi.getJournalEntryReportExportFlatData(reportId);

    if (res.data.errCode) {
      toast.warn(displayErrorText(res.data.errCode));
      setLoading(false);
      return false;
    }

    const records = res.data.body ?? [];
    if (records.length === 0) {
      toast.warn('לא נמצאו רשומות לייצוא בדיווח זה');
      setLoading(false);
      return false;
    }

    generateAndDownloadExcelFile([[...HEADERS], ...buildRows(records)], {
      fileName: `פקודות_יומן_דיווח_${reportId}`,
      sheetName: 'פקודות יומן',
    });

    toast.success('קובץ האקסל הורד בהצלחה');
    setLoading(false);
    return true;
  } catch (e) {
    console.error('Export journal entry report flat data Excel failed:', e);
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setLoading(false);
    return false;
  }
};
